import React from 'react';
import Themes from './Themes';
import Layout from '../../components/Layout';
import HeadMessage from '../../components/Common/HeadMessage/HeadMessage';
import Breadcrumbs from '../../components/Breadcrumbs';
import { SITE_URL } from '../../utils/seo';
import {
	DEFAULT_SORT,
	PER_PAGE,
	parseThemesQuery,
	themesUrl,
} from './themesQuery';

/**
 * /themes — список пользовательских тем.
 *
 * Что здесь решается по SEO:
 *
 *  - Пагинация индексируется, у каждой страницы свой canonical на саму
 *    себя. Схлопывать её на первую нельзя: тогда со второй и дальше
 *    робот не пойдёт, а там сотни тем.
 *  - Поиск (?q=), фильтр по версии (?made=) и сортировки, кроме
 *    основной, отдаются с noindex.
 *    Это одна и та же выборка в другом порядке: в индексе от неё только
 *    дубли, а вот ссылки со страницы обойти стоит — отсюда follow.
 *  - Параметры по умолчанию в URL не пишутся, см. themesUrl.
 */
async function action({ fetch, query }) {
	const { page, sort, search, order, made, madeWith } = parseThemesQuery(
		query,
	);
	const filterArgs = [
		search ? `search:${JSON.stringify(search)}` : '',
		madeWith ? `madeWith:${JSON.stringify(madeWith)}` : '',
	].filter(Boolean);

	const listQuery = [
		'{themes(',
		`page:${page}, limit:${PER_PAGE}, order:"${order}"`,
		filterArgs.length ? `, ${filterArgs.join(', ')}` : '',
		'){id,title,installs,description,workspace,madeWith}',
		filterArgs.length
			? ` themesCount(${filterArgs.join(', ')})`
			: ' themesCount',
		'}',
	].join('');

	const resp = await fetch('/graphql', {
		body: JSON.stringify({ query: listQuery }),
	});
	const { data } = await resp.json();

	if (!data || !data.themes) throw new Error('Failed to load themes.');

	const total = data.themesCount || data.themes.length;
	const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

	// Страница за пределами выборки — честный 404, а не пустая сетка:
	// иначе получаем бесконечное число пустых URL под индексацию
	if (page > totalPages && page > 1) {
		return undefined;
	}

	const indexable = !search && !made && sort === DEFAULT_SORT;

	const title =
		page > 1
			? `New Tab Themes — page ${page}`
			: 'New Tab Themes Made by Users';

	const description =
		page > 1
			? `Page ${page} of new tab pages built with New Tab Studio. Install any of them in one click — wallpaper, widgets and layout included.`
			: 'Browse new tab pages other people have built with New Tab Studio and install any of them in one click — wallpaper, widgets, positions and styling included.';

	return {
		title,
		description,
		chunks: ['themes'],
		// Собираем сами, а не через canonicalUrl: та срезает query-строку —
		// она рассчитана на пути без параметров. Здесь же параметры и есть
		// адрес страницы, и каждая страница пагинации должна ссылаться
		// на себя, иначе робот дальше первой не пойдёт.
		canonical: `${SITE_URL}${themesUrl({ page, sort, search, made })}`,
		robots: indexable ? undefined : 'noindex, follow',
		jsonLd: indexable
			? [
					{
						'@context': 'https://schema.org',
						'@type': 'CollectionPage',
						'@id': `${SITE_URL}${themesUrl({ page })}#webpage`,
						url: `${SITE_URL}${themesUrl({ page })}`,
						name: title,
						description,
						isPartOf: { '@id': `${SITE_URL}/#website` },
					},
			  ]
			: [],
		component: (
			<Layout>
				<Breadcrumbs
					items={[
						{ name: 'Home', url: '/' },
						page > 1
							? { name: 'Themes', url: '/themes' }
							: { name: 'Themes' },
						...(page > 1 ? [{ name: `Page ${page}` }] : []),
					]}
				/>

				<HeadMessage variant="compact">
					<h1>
						New tab <span>themes</span> made by users
					</h1>
					<h5>
						Pages people built and shared. Install any of them in
						one click, then change the dates and lists to yours.
					</h5>
				</HeadMessage>

				<Themes
					themes={data.themes}
					total={total}
					page={page}
					sort={sort}
					search={search}
					made={made}
				/>
			</Layout>
		),
	};
}

export default action;
